import type { FC } from "react"
import { useMemo } from "react"

import "leaflet/dist/leaflet.css"

import type { RecordType } from "types/dashboard.types"
import {
  formatDeviceLatLonData,
  getLatLonDataFromRecord,
  getMapCenterFromDeviceLocation,
} from "helpers/utils/map"

import useDevices from "helpers/hooks/useDevices"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import { getCellHeight } from "helpers/utils/dashboards"
import Spinner from "widgets/common/Spinner"
import DeviceMapMarkers from "./DeviceMapMarkers"
import Map from "./Map"

import "./map.css"

interface GeoPlotProps {
  data: RecordType[]
}

const GeoPlot: FC<GeoPlotProps> = ({ data }) => {
  const { rowHeight, config } = useDashboardContext()
  const { devsWRelsByIdWStates } = useDevices()

  const latlonData = useMemo(() => getLatLonDataFromRecord(data), [data])

  const formattedDevicesData = useMemo(
    () => formatDeviceLatLonData(latlonData, devsWRelsByIdWStates),
    [devsWRelsByIdWStates, latlonData],
  )

  const mapHeight = useMemo(() => {
    if (!config) {
      return "inherit"
    }
    if (rowHeight) {
      return `calc(${getCellHeight("LOCATION", config.type) * rowHeight * 0.9}px)`
    }
    return "inherit"
  }, [config, rowHeight])

  const mapKey = useMemo(() => `map-${mapHeight}`, [mapHeight])

  const center = useMemo(
    () => getMapCenterFromDeviceLocation(formattedDevicesData),
    [formattedDevicesData],
  )

  return (
    <>
      {data ? (
        <Map key={mapKey} center={center} mapHeight={mapHeight}>
          <DeviceMapMarkers devices={formattedDevicesData} />
        </Map>
      ) : (
        <Spinner />
      )}
    </>
  )
}

export default GeoPlot
